import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Trash2, Receipt, CheckCircle, Clock, AlertTriangle, Upload, FileText, Download, Eye, Pencil, Lock, Unlock, History, Send, BookCheck, CreditCard, Search as SearchIcon, ChevronDown, ChevronRight, LayoutList, ListTree, Building2, Mail } from 'lucide-react';
import PagamentiDialog from '@/components/PagamentiDialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { format, parseISO, differenceInDays, isValid } from 'date-fns';
import { fmtDate } from '@/lib/dateFmt';
import { getFileUrl } from '@/utils/fileUrl';
import { FilePreview } from '@/components/FilePreview';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import { BulkActionToolbar } from '@/components/BulkActionToolbar';
import { SafeTableBoundary } from '@/components/SafeTableBoundary';
import { AccontoSelector } from '@/components/AccontoSelector';
import { CantiereSelectByCliente } from '@/components/CantiereSelectByCliente';
import { AndamentoMensileFatture } from '@/components/AndamentoMensileFatture';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Aliquota IVA standard usata come fallback quando il documento non la specifica.
// In Italia l'IVA ordinaria è il 22% (Legge 17/06/1973 n.286). Le altre aliquote
// (4% / 5% / 10%) sono mantenute negli SelectItem dell'editor fattura.
const VAT_DEFAULT_PERCENTAGE = 22;

const statoBadge = {
  emessa: { label: 'Emessa', className: 'bg-blue-100 text-blue-700 border-blue-200', icon: Clock },
  parzialmente_incassata: { label: 'Parzialmente incassata', className: 'bg-indigo-100 text-indigo-700 border-indigo-200', icon: Clock },
  incassata: { label: 'Incassata', className: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle },
  scaduta: { label: 'Scaduta', className: 'bg-red-100 text-red-700 border-red-200', icon: AlertTriangle },
  stornata: { label: 'Stornata', className: 'bg-slate-200 text-slate-700 border-slate-300', icon: AlertTriangle },
  annullata: { label: 'Annullata', className: 'bg-slate-100 text-slate-500 border-slate-200', icon: Clock },
};

export default function FattureUscitaPage({ forceTipo, title, subtitle }) {
  const { user } = useAuth();
  const isSuper = !!user?.is_super_admin;
  const [fatture, setFatture] = useState([]);
  // iter48: lista completa (non filtrata da forceTipo) usata SOLO dal dropdown
  // "Fattura di riferimento" della nota di credito → permette di selezionare
  // anche fatture normali quando siamo nella pagina forceTipo='nota_credito'.
  const [tutteFatture, setTutteFatture] = useState([]);
  const [riferimentoOpen, setRiferimentoOpen] = useState(false);
  const [riferimentoQuery, setRiferimentoQuery] = useState('');
  const [clienti, setClienti] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [logOpen, setLogOpen] = useState(false);
  const [logEntries, setLogEntries] = useState([]);
  const [logDoc, setLogDoc] = useState(null);
  // iter56: deep-link ?q=NUMERO per filtrare/evidenziare una specifica fattura
  const initialDeepLinkQ = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('q') || '' : '';
  const [deepLinkQuery, setDeepLinkQuery] = useState(initialDeepLinkQ);
  // iter57: link manuale Conto Emesso di riferimento (caricato per cantiere selezionato)
  const [contiDisponibili, setContiDisponibili] = useState([]);
  const [selectedContoId, setSelectedContoId] = useState('');
  const initialTipo = forceTipo || 'fattura';
  const [form, setForm] = useState({
    cliente: '', cliente_id: '', numero: '', data: new Date().toISOString().split('T')[0],
    scadenza: '', imponibile: '', aliquota_iva: '22', descrizione: '', cantiere_id: '', file_url: '', tipo_documento: initialTipo, riferimento_fattura_id: '', acconto_riferimento: ''
  });

  // Auto-fill form when a reference invoice is selected for a credit note
  const handleSelectRiferimento = (fid) => {
    if (!fid) { setForm(prev => ({...prev, riferimento_fattura_id: ''})); return; }
    // iter48: cerca in `tutteFatture` (sempre completa) NON in `fatture` (filtrata)
    const ref = (tutteFatture || []).find(f => f.id === fid) || (fatture || []).find(f => f.id === fid);
    if (!ref) { setForm(prev => ({...prev, riferimento_fattura_id: fid})); return; }
    setForm(prev => ({
      ...prev,
      riferimento_fattura_id: fid,
      cliente: ref.cliente || prev.cliente,
      cliente_id: ref.cliente_id || prev.cliente_id,
      imponibile: ref.imponibile ?? prev.imponibile,
      aliquota_iva: String(ref.aliquota_iva || VAT_DEFAULT_PERCENTAGE),
      cantiere_id: ref.cantiere_id || prev.cantiere_id,
      descrizione: `Storno fattura ${ref.numero} - ${prev.descrizione || ''}`.trim(),
    }));
    setRiferimentoOpen(false);
    setRiferimentoQuery('');
  };

  useEffect(() => { fetchAll(); }, [forceTipo]);  // eslint-disable-line react-hooks/exhaustive-deps

  // iter100g: invio manuale sollecito fattura
  const sendReminder = async (f, kind) => {
    if (!window.confirm(`Inviare un ${kind === 'pre_7d' ? 'promemoria di scadenza' : 'sollecito di pagamento'} al cliente ${f.cliente} per la fattura ${f.numero}?`)) return;
    try {
      await axios.post(`${API}/fatture-uscita/${f.id}/send-reminder?kind=${kind}`, {}, { withCredentials: true });
      toast.success(kind === 'pre_7d' ? 'Promemoria inviato al cliente' : 'Sollecito inviato al cliente');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore invio email');
    }
  };

  // iter57/iter77: carica TUTTI i conti emessi (qualsiasi cantiere) per il combobox link manuale
  useEffect(() => {
    if (!dialogOpen) {
      setContiDisponibili([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/conti-emessi/all`, { withCredentials: true });
        if (cancelled) return;
        const items = Array.isArray(res.data?.items) ? res.data.items : [];
        // Conti dello stesso cantiere della fattura in cima
        items.sort((a, b) => {
          const aSame = form.cantiere_id && a.cantiere_id === form.cantiere_id ? 0 : 1;
          const bSame = form.cantiere_id && b.cantiere_id === form.cantiere_id ? 0 : 1;
          if (aSame !== bSame) return aSame - bSame;
          return (b.data_emissione || '').localeCompare(a.data_emissione || '');
        });
        setContiDisponibili(items);
      } catch {
        if (!cancelled) setContiDisponibili([]);
      }
    })();
    return () => { cancelled = true; };
  }, [dialogOpen, form.cantiere_id]);

  const fetchAll = async () => {
    try {
      const [fattureRes, clientiRes, cantieriRes] = await Promise.all([
        axios.get(`${API}/fatture-uscita`, { withCredentials: true }),
        axios.get(`${API}/clienti`, { withCredentials: true }),
        axios.get(`${API}/cantieri`, { withCredentials: true })
      ]);
      const all = fattureRes.data || [];
      setTutteFatture(all);  // iter48: lista completa per il dropdown nota credito
      // iter83-fix2: filtro robusto — trattiamo come "fattura" qualsiasi cosa
      // NON sia esattamente 'nota_credito' (copre valori legacy/spuri).
      let filtered = all;
      if (forceTipo === 'nota_credito') {
        filtered = all.filter(f => f.tipo_documento === 'nota_credito');
      } else if (forceTipo === 'fattura') {
        filtered = all.filter(f => f.tipo_documento !== 'nota_credito');
      }
      setFatture(filtered);
      setClienti(clientiRes.data);
      setCantieri(cantieriRes.data);
    } catch { toast.error('Errore nel caricamento'); }
    finally { setLoading(false); }
  };

  const openLog = async (f) => {
    try {
      const res = await axios.get(`${API}/fatturazione/fatture_uscita/${f.id}/log`, { withCredentials: true });
      setLogEntries(res.data.log || []);
      setLogDoc({ protocollo: res.data.protocollo, numero: res.data.numero });
      setLogOpen(true);
    } catch { toast.error('Errore caricamento cronologia'); }
  };

  const lockSdi = async (f) => {
    if (!window.confirm(`Vuoi marcare il documento ${f.protocollo || f.numero} come INVIATO A SDI?\n\nATTENZIONE: una volta bloccato, il documento NON sarà più modificabile né eliminabile. Per rettificarlo dovrai emettere una nota di credito.`)) return;
    try {
      await axios.post(`${API}/fatturazione/fatture_uscita/${f.id}/sdi-lock`, {}, { withCredentials: true });
      toast.success('Documento bloccato e marcato come inviato a SDI');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  // Sblocca una fattura inviata a SDI per consentirne la modifica (solo super-admin)
  const unlockSdi = async (f) => {
    if (!window.confirm(`Sbloccare il documento ${f.protocollo || f.numero} per modificarlo?\n\nÈ stato inviato a SDI: modificalo solo se strettamente necessario. Lo sblocco verrà registrato nello storico.`)) return;
    try {
      await axios.post(`${API}/fatturazione/fatture_uscita/${f.id}/sdi-unlock`, {}, { withCredentials: true });
      toast.success('Documento sbloccato: ora è modificabile');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const toggleContabilizza = async (f) => {
    try {
      await axios.post(`${API}/fatturazione/fatture_uscita/${f.id}/contabilizza`, {}, { withCredentials: true });
      toast.success('Stato contabilizzazione aggiornato');
      fetchAll();
    } catch { toast.error('Errore'); }
  };

  const handleImportFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImportFile(f);
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', f);
      const res = await axios.post(`${API}/fatture-uscita/import`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      const data = res.data;
      setForm(prev => ({
        ...prev,
        cliente: data.cliente || prev.cliente,
        numero: data.numero || prev.numero,
        data: data.data || prev.data,
        scadenza: data.scadenza || prev.scadenza,
        imponibile: data.imponibile ? String(data.imponibile) : prev.imponibile,
        aliquota_iva: data.aliquota_iva ? String(data.aliquota_iva) : prev.aliquota_iva,
        descrizione: data.descrizione || prev.descrizione,
        file_url: data.file_url || prev.file_url,
      }));
      toast.success('Dati estratti dal file');
      setDialogOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nell\'importazione');
    } finally { setImporting(false); }
  };

  const handleClienteChange = (clienteId) => {
    const cliente = clienti.find(c => c.id === clienteId);
    if (cliente) {
      setForm({ ...form, cliente_id: clienteId, cliente: cliente.ragione_sociale });
    }
  };

  const handleUploadFile = async () => {
    if (!file) return '';
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/fatture-uscita/upload`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      return res.data.url;
    } catch { toast.error('Errore upload'); return ''; }
    finally { setUploading(false); }
  };

  const [editingId, setEditingId] = useState(null);

  const openEdit = (f) => {
    setEditingId(f.id);
    setForm({
      cliente: f.cliente || '',
      cliente_id: f.cliente_id || '',
      numero: f.numero || '',
      data: f.data || '',
      scadenza: f.scadenza || '',
      imponibile: String(f.imponibile ?? ''),
      aliquota_iva: String(f.aliquota_iva ?? VAT_DEFAULT_PERCENTAGE),
      descrizione: f.descrizione || '',
      cantiere_id: f.cantiere_id || '',
      file_url: f.file_url || '',
      tipo_documento: f.tipo_documento || 'fattura',
      riferimento_fattura_id: f.riferimento_fattura_id || '',
      acconto_riferimento: f.acconto_riferimento || '',
    });
    setSelectedContoId(f.conto_emesso_id || '');
    setFile(null);
    setDialogOpen(true);
  };

  const openNew = () => {
    setEditingId(null);
    setForm({ cliente: '', cliente_id: '', numero: '', data: new Date().toISOString().split('T')[0], scadenza: '', imponibile: '', aliquota_iva: String(VAT_DEFAULT_PERCENTAGE), descrizione: '', cantiere_id: '', file_url: '', tipo_documento: forceTipo || 'fattura', riferimento_fattura_id: '', acconto_riferimento: '' });
    setFile(null);
    setDialogOpen(true);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.cliente || !form.numero || !form.imponibile) {
      toast.error('Compila cliente, numero e imponibile');
      return;
    }
    try {
      let file_url = form.file_url;
      if (file) { file_url = await handleUploadFile(); }
      const imponibile = parseFloat(form.imponibile);
      const aliquota = parseFloat(form.aliquota_iva || VAT_DEFAULT_PERCENTAGE);
      const iva = Math.round(imponibile * aliquota / 100 * 100) / 100;
      const importo = Math.round((imponibile + iva) * 100) / 100;
      const payload = { ...form, imponibile, iva, importo, aliquota_iva: aliquota, file_url };
      let savedFatturaId = editingId;
      if (editingId) {
        await axios.put(`${API}/fatture-uscita/${editingId}`, payload, { withCredentials: true });
        toast.success('Fattura aggiornata');
      } else {
        const res = await axios.post(`${API}/fatture-uscita`, payload, { withCredentials: true });
        savedFatturaId = res?.data?.id || null;
        toast.success('Fattura creata');
      }
      // iter57: sincronizza link al Conto Emesso (se cambiato/aggiunto/rimosso)
      if (savedFatturaId) {
        try {
          await axios.post(
            `${API}/fatture-uscita/${savedFatturaId}/link-conto`,
            { conto_emesso_id: selectedContoId || null },
            { withCredentials: true }
          );
        } catch (linkErr) {
          // non bloccare il salvataggio per errore link
          if (linkErr?.response?.status !== 404) {
            toast.error('Fattura salvata ma collegamento al conto fallito: ' + (linkErr?.response?.data?.detail || ''));
          }
        }
      }
      setDialogOpen(false);
      setEditingId(null);
      setFile(null);
      setForm({ cliente: '', cliente_id: '', numero: '', data: new Date().toISOString().split('T')[0], scadenza: '', imponibile: '', aliquota_iva: '22', descrizione: '', cantiere_id: '', file_url: '', tipo_documento: 'fattura', riferimento_fattura_id: '', acconto_riferimento: '' });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const handleChangeStato = async (id, stato) => {
    try {
      await axios.put(`${API}/fatture-uscita/${id}/stato?stato=${stato}`, {}, { withCredentials: true });
      toast.success('Stato aggiornato');
      fetchAll();
    } catch { toast.error('Errore'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa fattura?')) return;
    try {
      await axios.delete(`${API}/fatture-uscita/${id}`, { withCredentials: true });
      toast.success('Fattura eliminata');
      fetchAll();
    } catch { toast.error('Errore'); }
  };

  // iter47: pagamenti parziali (incassi)
  const [pagamentiOpen, setPagamentiOpen] = useState(false);
  const [pagamentiFattura, setPagamentiFattura] = useState(null);
  const openPagamenti = (f) => { setPagamentiFattura(f); setPagamentiOpen(true); };

  // iter85: link cantiere anche quando SDI-locked
  // iter86-k: aggiunto acconto_riferimento nel dialog link cantiere
  const [linkCantiereFattura, setLinkCantiereFattura] = useState(null);
  const [linkCantiereValue, setLinkCantiereValue] = useState('');
  const [linkAccontoValue, setLinkAccontoValue] = useState('');
  const openLinkCantiere = (f) => {
    setLinkCantiereFattura(f);
    setLinkCantiereValue(f.cantiere_id || '');
    setLinkAccontoValue(f.acconto_riferimento || '');
  };
  const saveLinkCantiere = async () => {
    if (!linkCantiereFattura) return;
    try {
      const params = new URLSearchParams({
        cantiere_id: linkCantiereValue || '',
        acconto_riferimento: linkCantiereValue ? (linkAccontoValue || '') : '',
      });
      await axios.post(
        `${API}/fatture-uscita/${linkCantiereFattura.id}/link-cantiere?${params.toString()}`,
        {},
        { withCredentials: true }
      );
      toast.success('Cantiere aggiornato');
      setLinkCantiereFattura(null);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore aggiornamento cantiere');
    }
  };

  // Bulk selection (mass-delete with soft-delete on backend)
  const bulkSel = useBulkSelection(fatture, (f) => f.id);

  const formatDate = (d) => {
    if (!d) return '-';
    const head = String(d).slice(0, 10).replace(/\//g, '-');
    const parts = head.split('-');
    return parts.length === 3 && parts.every(Boolean)
      ? `${parts[2]}-${parts[1]}-${parts[0]}`
      : String(d);
  };
  const formatEuro = (v) => parseFloat(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // iter51: aggregato conti emessi da fatturare (visibile solo su Fatture Emesse)
  const [contiEmessiStats, setContiEmessiStats] = useState(null);
  useEffect(() => {
    if (forceTipo !== 'fattura') return;
    axios.get(`${API}/conti-emessi/all?stato=bozza,inviato`, { withCredentials: true })
      .then(r => setContiEmessiStats(r.data || null))
      .catch(() => {});
  }, [forceTipo]);

  const totals = useMemo(() => {
    let emesse = 0, incassate = 0, scadute = 0;
    for (const f of fatture) {
      const imp = f.importo || 0;
      const pagato = parseFloat(f.totale_pagato || 0);
      if (f.stato === 'emessa') emesse += imp;
      else if (f.stato === 'parzialmente_incassata') {
        // iter51: la quota pagata va negli "incassate", il residuo nelle "emesse" (da incassare)
        incassate += pagato;
        emesse += Math.max(0, imp - pagato);
      }
      else if (f.stato === 'incassata') incassate += imp;
      else if (f.stato === 'scaduta') scadute += imp;
    }    return { emesse, incassate, scadute };
  }, [fatture]);
  const totEmesse = totals.emesse;
  const totIncassate = totals.incassate;
  const totScadute = totals.scadute;

  // iter84: filtro mese + raggruppamento per mese (richiesta utente).
  // NOTA: gli hooks devono stare PRIMA dell'early return `if (loading)`.
  const [searchQ, setSearchQ] = useState('');
  const [filtroMese, setFiltroMese] = useState('tutti');
  // iter86-r: filtro pagato/non pagato — 'tutte' | 'pagate' | 'non_pagate'
  const [filtroStato, setFiltroStato] = useState('tutte');
  const [raggruppaMese, setRaggruppaMese] = useState(() => {
    try { return localStorage.getItem('fatture_uscita_raggruppa_mese') === '1'; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('fatture_uscita_raggruppa_mese', raggruppaMese ? '1' : '0'); }
    catch { /* ignore */ }
  }, [raggruppaMese]);
  const [mesiCollassati, setMesiCollassati] = useState(new Set());
  const toggleMese = (key) => {
    setMesiCollassati(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const meseKeyOf = (f) => {
    const d = f?.data;
    if (typeof d === 'string' && /^\d{4}-\d{2}/.test(d)) return d.slice(0, 7);
    return 'senza-data';
  };
  const meseLabel = (key) => {
    if (key === 'senza-data') return 'Senza data';
    const [y, m] = key.split('-');
    const names = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];
    const idx = parseInt(m, 10) - 1;
    return `${names[idx] || m} ${y}`;
  };
  const mesiDisponibili = useMemo(() => {
    const set = new Set();
    for (const f of fatture) set.add(meseKeyOf(f));
    return Array.from(set).sort((a, b) => {
      if (a === 'senza-data') return 1;
      if (b === 'senza-data') return -1;
      return b.localeCompare(a);
    });
  }, [fatture]);
  const safeIncludes = (v, needle) => String(v == null ? '' : v).toLowerCase().includes(needle);
  const fattureVisibili = useMemo(() => {
    let list = fatture;
    // deepLinkQuery (arrivo da Conto Emesso) è sempre applicato per retro-compat
    if (deepLinkQuery) {
      const q = String(deepLinkQuery).toLowerCase();
      list = list.filter(f => safeIncludes(f.numero, q));
    }
    const q = searchQ.trim().toLowerCase();
    if (q) {
      list = list.filter(f => (
        safeIncludes(f.numero, q) ||
        safeIncludes(f.cliente, q) ||
        safeIncludes(f.protocollo, q) ||
        safeIncludes(f.descrizione, q)
      ));
    }
    if (filtroMese && filtroMese !== 'tutti') {
      list = list.filter(f => meseKeyOf(f) === filtroMese);
    }
    // iter86-r: filtro stato pagamento (incassato)
    if (filtroStato === 'pagate') {
      list = list.filter(f => f.stato === 'incassata');
    } else if (filtroStato === 'non_pagate') {
      list = list.filter(f => f.stato !== 'incassata' && f.stato !== 'stornata');
    } else if (filtroStato === 'scadute') {
      // iter86-s: fatture con scadenza passata e non ancora incassate
      list = list.filter(f => {
        if (f.stato === 'incassata' || f.stato === 'stornata') return false;
        if (f.stato === 'scaduta') return true;
        if (!f.scadenza || typeof f.scadenza !== 'string') return false;
        try {
          const parsed = parseISO(f.scadenza);
          if (!isValid(parsed)) return false;
          return differenceInDays(parsed, new Date()) < 0;
        } catch { return false; }
      });
    }
    return list;
  }, [fatture, searchQ, filtroMese, filtroStato, deepLinkQuery]);
  // iter86-r: contatori per le pill dei filtri (calcolati su tutte le fatture)
  // iter86-u: aggiunti anche i TOTALI in euro per ogni gruppo.
  const contatoriStato = useMemo(() => {
    const isScaduta = (f) => {
      if (f.stato === 'incassata' || f.stato === 'stornata') return false;
      if (f.stato === 'scaduta') return true;
      if (!f.scadenza || typeof f.scadenza !== 'string') return false;
      try {
        const parsed = parseISO(f.scadenza);
        if (!isValid(parsed)) return false;
        return differenceInDays(parsed, new Date()) < 0;
      } catch { return false; }
    };
    let pagate = 0, nonPagate = 0, scadute = 0;
    let totTutte = 0, totPagate = 0, totNonPagate = 0, totScadute = 0;
    for (const f of fatture) {
      const imp = Number(f.importo) || 0;
      totTutte += imp;
      if (f.stato === 'incassata') { pagate++; totPagate += imp; }
      else if (f.stato !== 'stornata') {
        nonPagate++; totNonPagate += imp;
        if (isScaduta(f)) { scadute++; totScadute += imp; }
      }
    }
    return {
      tutte: fatture.length, pagate, nonPagate, scadute,
      totTutte, totPagate, totNonPagate, totScadute,
    };
  }, [fatture]);
  const gruppiMese = useMemo(() => {
    if (!raggruppaMese) return [];
    const map = new Map();
    for (const f of fattureVisibili) {
      const key = meseKeyOf(f);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(f);
    }
    const keys = Array.from(map.keys()).sort((a, b) => {
      if (a === 'senza-data') return 1;
      if (b === 'senza-data') return -1;
      return b.localeCompare(a);
    });
    return keys.map(k => {
      const rows = map.get(k);
      const totale = rows.reduce((s, f) => {
        const imp = Number(f.importo) || 0;
        return f.tipo_documento === 'nota_credito' ? s - imp : s + imp;
      }, 0);
      return { key: k, label: meseLabel(k), rows, totale };
    });
  }, [raggruppaMese, fattureVisibili]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;

  // iter84: renderer di riga estratto — usato sia dalla vista piatta sia da
  // quella raggruppata per mese. String coercion difensivo su tutti i campi.
  const numeroStr = (v) => String(v == null ? '' : v);
  const renderFatturaRow = (f) => {
    const stato = statoBadge[f.stato] || statoBadge.emessa;
    const Icon = stato.icon;
    const highlight = deepLinkQuery && String(f.numero || '').toLowerCase().includes(String(deepLinkQuery).toLowerCase());
    return (
      <TableRow key={f.id} data-testid={`fattura-uscita-${f.id}`} data-selected={bulkSel.isSelected(f.id) ? 'true' : 'false'} className={highlight ? 'bg-amber-50 hover:bg-amber-100' : ''}>
        <TableCell>
          <Checkbox
            checked={bulkSel.isSelected(f.id)}
            onCheckedChange={() => bulkSel.toggle(f.id)}
            aria-label={`Seleziona fattura ${numeroStr(f.numero)}`}
            data-testid={`bulk-select-fattura-${f.id}`}
          />
        </TableCell>
        <TableCell className="font-mono text-xs">
          <div className="flex items-center gap-1">
            {f.locked && <Lock className="h-3 w-3 text-slate-400" />}
            <span className={f.protocollo ? 'text-slate-700' : 'text-slate-300'}>{numeroStr(f.protocollo) || '—'}</span>
          </div>
        </TableCell>
        <TableCell className="font-mono font-medium">
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-1">
              <span className="break-all">{numeroStr(f.numero)}</span>
              {f.tipo_documento === 'nota_credito' && !forceTipo && <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">N. Credito</Badge>}
            </div>
            {f.conto_emesso_numero && (
              <a
                href={`/admin/conti-emessi?q=${encodeURIComponent(f.conto_emesso_numero)}`}
                className="text-[10px] text-amber-700 hover:text-amber-900 underline decoration-dotted inline-flex items-center gap-0.5 w-fit"
                title={`Apri Conto Emesso ${f.conto_emesso_numero}`}
                data-testid={`link-conto-${f.id}`}
              >
                <Receipt className="h-2.5 w-2.5" />
                Da Conto {f.conto_emesso_numero}
              </a>
            )}
          </div>
        </TableCell>
        <TableCell>{formatDate(f.data)}</TableCell>
        <TableCell className="font-medium">{numeroStr(f.cliente)}</TableCell>
        <TableCell className="text-right">{f.tipo_documento === 'nota_credito' ? '-' : ''}{formatEuro(f.imponibile)}</TableCell>
        <TableCell className="text-right text-slate-500">{f.tipo_documento === 'nota_credito' ? '-' : ''}{formatEuro(f.iva)}</TableCell>
        <TableCell className={`text-right font-bold ${f.tipo_documento === 'nota_credito' ? 'text-amber-700' : ''}`}>
          <div className="flex flex-col items-end">
            <span>{f.tipo_documento === 'nota_credito' ? '-' : ''}{formatEuro(f.importo)}</span>
            {f.stato === 'parzialmente_incassata' && (Number(f.totale_pagato) || 0) > 0 && (
              <span className="text-[10px] text-indigo-700 font-mono font-normal mt-0.5" data-testid={`parziale-progress-uscita-${f.id}`}>
                {formatEuro(f.totale_pagato)} / {formatEuro(f.importo)}
              </span>
            )}
          </div>
        </TableCell>
        <TableCell>{formatDate(f.scadenza)}</TableCell>
        <TableCell>
          <div className="flex flex-col gap-0.5">
            <Badge variant="outline" className={`${stato.className} text-xs`}>
              <Icon className="h-3 w-3 mr-1" />{stato.label}
            </Badge>
            <div className="flex flex-wrap gap-1 mt-0.5">
              <Badge variant="outline" className={`text-[10px] py-0 px-1 ${f.stato_sdi === 'inviata' || f.stato_sdi === 'accettata' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>SDI: {numeroStr(f.stato_sdi) || 'non inv.'}</Badge>
              <Badge variant="outline" className={`text-[10px] py-0 px-1 ${f.stato_contabilizzazione === 'registrata' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>Cont: {f.stato_contabilizzazione === 'registrata' ? 'reg.' : 'no'}</Badge>
            </div>
          </div>
        </TableCell>
        <TableCell className="text-right">
          <div className="flex items-center justify-end gap-0.5">
            {f.file_url && (
              <>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setPreviewUrl(f.file_url); setPreviewTitle(`Doc ${numeroStr(f.numero)}`); setPreviewOpen(true); }} data-testid={`preview-fattura-${f.id}`}><Eye className="h-3.5 w-3.5" /></Button>
                <a href={getFileUrl(f.file_url)} target="_blank" rel="noopener noreferrer">
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0"><Download className="h-3.5 w-3.5" /></Button>
                </a>
              </>
            )}
            {f.stato === 'emessa' && (
              <Button size="sm" variant="ghost" className="h-7 text-emerald-600 text-xs" onClick={() => handleChangeStato(f.id, 'incassata')}>Incassa</Button>
            )}
            {f.stato === 'scaduta' && (
              <Button size="sm" variant="ghost" className="h-7 text-emerald-600 text-xs" onClick={() => handleChangeStato(f.id, 'incassata')}>Incassa</Button>
            )}
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-indigo-600 hover:bg-indigo-50" onClick={() => openPagamenti(f)} title="Gestisci incassi parziali" data-testid={`pagamenti-fattura-uscita-${f.id}`}><CreditCard className="h-3.5 w-3.5" /></Button>
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-amber-600 hover:bg-amber-50 relative" onClick={() => openLinkCantiere(f)} title={f.cantiere_id ? `Cambia cantiere${f.acconto_riferimento ? ' · Acconto: ' + f.acconto_riferimento : ''}` : 'Collega a cantiere'} data-testid={`link-cantiere-fattura-uscita-${f.id}`}>
              <Building2 className="h-3.5 w-3.5" />
              {f.acconto_riferimento && <span className="absolute -top-1 -right-1 text-[9px] leading-none bg-amber-500 text-white rounded-full min-w-[14px] h-3.5 px-1 flex items-center justify-center font-semibold pointer-events-none">{String(f.acconto_riferimento).startsWith('extra') ? 'E' : String(f.acconto_riferimento)}</span>}
            </Button>
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-600" onClick={() => openLog(f)} data-testid={`log-fu-${f.id}`} title="Cronologia"><History className="h-3.5 w-3.5" /></Button>
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-emerald-600" onClick={() => toggleContabilizza(f)} title="Contabilizza"><BookCheck className="h-3.5 w-3.5" /></Button>
            {/* iter100g: Sollecita (solo per non-pagate con scadenza valorizzata) */}
            {f.stato !== 'pagata' && f.stato !== 'incassata' && f.stato !== 'stornata' && f.scadenza && (() => {
              const daysToScad = differenceInDays(parseISO(f.scadenza), new Date());
              const kind = daysToScad < 0 ? 'post_30d' : 'pre_7d';
              const badgeSent = (f.reminders_sent || {})[kind];
              const label = daysToScad < 0 ? 'Invia sollecito (scaduta)' : 'Invia promemoria (in scadenza)';
              return (
                <Button size="sm" variant="ghost" className={`h-7 w-7 p-0 ${badgeSent ? 'text-slate-400' : (daysToScad < 0 ? 'text-red-600' : 'text-amber-600')}`} onClick={() => sendReminder(f, kind)} title={badgeSent ? `Già inviato il ${new Date(badgeSent).toLocaleDateString('it-IT')}` : label} data-testid={`send-reminder-${f.id}`}>
                  <Mail className="h-3.5 w-3.5" />
                </Button>
              );
            })()}
            {!f.locked && (
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-blue-600" onClick={() => lockSdi(f)} data-testid={`lock-sdi-fu-${f.id}`} title="Invia a SDI e blocca"><Send className="h-3.5 w-3.5" /></Button>
            )}
            {f.locked && isSuper && (
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-amber-600" onClick={() => unlockSdi(f)} data-testid={`unlock-sdi-fu-${f.id}`} title="Sblocca per modificare"><Unlock className="h-3.5 w-3.5" /></Button>
            )}
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-600 disabled:opacity-30" onClick={() => openEdit(f)} data-testid={`edit-fattura-uscita-${f.id}`} title={f.locked ? 'Bloccato (SDI)' : 'Modifica'} disabled={f.locked}><Pencil className="h-3.5 w-3.5" /></Button>
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 disabled:opacity-30" onClick={() => handleDelete(f.id)} data-testid={`delete-fattura-${f.id}`} disabled={f.locked} title={f.locked ? 'Bloccato (SDI)' : 'Elimina'}><Trash2 className="h-3.5 w-3.5" /></Button>
          </div>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <div className="space-y-6" data-testid="fatture-uscita-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">{title || (forceTipo === 'nota_credito' ? 'Note di Credito Emesse' : 'Fatture in Uscita')}</h1>
          <p className="text-slate-500 mt-1">{subtitle || (forceTipo === 'nota_credito' ? 'Note di credito emesse ai clienti (riducono il ricavo e l\'IVA a debito)' : 'Gestisci le fatture emesse ai clienti')}</p>
        </div>
        <div className="flex items-center gap-2">
          {!forceTipo || forceTipo === 'fattura' ? (
            <label className="cursor-pointer">
              <input type="file" accept=".pdf,.xml,.html,.p7m" className="hidden" onChange={handleImportFile} data-testid="import-fattura-file" />
              <div className="inline-flex items-center justify-center h-10 px-4 py-2 rounded-md border border-slate-200 bg-white text-sm font-medium hover:bg-slate-50 transition-colors">
                <Upload className="h-4 w-4 mr-2" />{importing ? 'Importazione...' : 'Importa PDF/XML'}
              </div>
            </label>
          ) : null}
          <Button className="bg-slate-900 hover:bg-slate-800 text-white" onClick={openNew} data-testid="add-fattura-uscita-btn">
            <Plus className="h-4 w-4 mr-2" />{forceTipo === 'nota_credito' ? 'Nuova Nota di Credito' : 'Nuova Fattura'}
          </Button>
        </div>
      </div>

      {/* Totali */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <p className="text-xs text-blue-600 uppercase">Da Incassare</p>
            <p className="text-2xl font-bold text-blue-700">{formatEuro(totEmesse)}</p>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="p-4">
            <p className="text-xs text-emerald-600 uppercase">Incassate</p>
            <p className="text-2xl font-bold text-emerald-700">{formatEuro(totIncassate)}</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <p className="text-xs text-red-600 uppercase">Scadute</p>
            <p className="text-2xl font-bold text-red-700">{formatEuro(totScadute)}</p>
          </CardContent>
        </Card>
      </div>

      {/* iter51: Banner Conti Emessi (solo su Fatture Emesse) */}
      {forceTipo === 'fattura' && contiEmessiStats && contiEmessiStats.count > 0 && (
        <Card className="border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50" data-testid="conti-emessi-banner">
          <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Receipt className="h-5 w-5 text-amber-700" />
              </div>
              <div>
                <p className="text-sm font-semibold text-amber-900">
                  {contiEmessiStats.count} {contiEmessiStats.count === 1 ? 'Conto Emesso' : 'Conti Emessi'} da fatturare
                </p>
                <p className="text-xs text-amber-700">
                  Totale lordo: <span className="font-mono font-bold">{formatEuro(contiEmessiStats.totale_lordo_complessivo)} €</span> — documenti preparatori da trasformare in Fattura Attiva
                </p>
              </div>
            </div>
            <a href="/admin/conti-emessi" data-testid="vai-conti-emessi-btn">
              <Button variant="outline" className="border-amber-300 text-amber-800 hover:bg-amber-100">
                Vedi Conti Emessi →
              </Button>
            </a>
          </CardContent>
        </Card>
      )}

      {/* iter54: Banner filtro deep-link (es. arrivo da Conto Emesso) */}
      {deepLinkQuery && (
        <Card className="border-amber-300 bg-amber-50" data-testid="deeplink-filter-banner">
          <CardContent className="p-3 flex items-center justify-between flex-wrap gap-2">
            <p className="text-sm text-amber-900">
              <strong>Filtro attivo:</strong> mostrando fatture con numero <code className="font-mono bg-amber-100 px-1.5 py-0.5 rounded">{deepLinkQuery}</code>
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setDeepLinkQuery(''); window.history.replaceState(null, '', window.location.pathname); }}
              className="text-amber-800 border-amber-300 hover:bg-amber-100"
              data-testid="clear-deeplink-filter"
            >
              Mostra tutte
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Tabella */}
      <BulkActionToolbar
        resource="fatture_uscita"
        selectedIds={bulkSel.selectedIds}
        onClear={bulkSel.clear}
        onDeleted={fetchAll}
        itemLabel="fattura"
      />
      <Card>
        <CardContent className="p-0">
          {/* iter84: toolbar con ricerca, filtro mese, raggruppamento */}
          <div className="p-3 border-b border-slate-200 flex flex-wrap items-center gap-2">
            <Input
              placeholder="Cerca per numero, cliente, protocollo, descrizione…"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              className="max-w-md flex-1 min-w-[200px]"
              data-testid="fatture-uscita-search"
            />
            <Select value={filtroMese} onValueChange={setFiltroMese}>
              <SelectTrigger className="w-[190px]" data-testid="fatture-uscita-filtro-mese">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tutti">Tutti i mesi</SelectItem>
                {mesiDisponibili.map(k => (
                  <SelectItem key={k} value={k}>{meseLabel(k)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              variant={raggruppaMese ? 'default' : 'outline'}
              onClick={() => setRaggruppaMese(v => !v)}
              className={raggruppaMese ? 'bg-slate-900 text-white' : ''}
              data-testid="fatture-uscita-toggle-raggruppa"
              title="Raggruppa fatture per mese con intestazioni collassabili"
            >
              {raggruppaMese ? <ListTree className="h-4 w-4 mr-1.5" /> : <LayoutList className="h-4 w-4 mr-1.5" />}
              {raggruppaMese ? 'Per mese' : 'Lista piatta'}
            </Button>
            {(searchQ || filtroMese !== 'tutti' || filtroStato !== 'tutte') && (
              <span className="text-xs text-slate-500" data-testid="fatture-uscita-search-count">
                {fattureVisibili.length} di {fatture.length} risultati
              </span>
            )}
            {(searchQ || filtroMese !== 'tutti' || filtroStato !== 'tutte') && (
              <Button size="sm" variant="ghost" onClick={() => { setSearchQ(''); setFiltroMese('tutti'); setFiltroStato('tutte'); }} data-testid="fatture-uscita-search-clear">
                Pulisci filtri
              </Button>
            )}
          </div>
          {/* iter86-r: filtri rapidi per stato (pagate / non pagate) */}
          <div className="px-3 py-2 border-b border-slate-100 bg-slate-50/60 flex flex-wrap items-center gap-1.5" data-testid="fatture-uscita-filtri-stato">
            {[
              { key: 'tutte', label: 'Tutte', count: contatoriStato.tutte, tot: contatoriStato.totTutte, cls: 'bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200', active: 'bg-slate-900 text-white border-slate-900 hover:bg-slate-800' },
              { key: 'pagate', label: 'Pagate', count: contatoriStato.pagate, tot: contatoriStato.totPagate, cls: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100', active: 'bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700' },
              { key: 'non_pagate', label: 'Non pagate', count: contatoriStato.nonPagate, tot: contatoriStato.totNonPagate, cls: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100', active: 'bg-amber-600 text-white border-amber-600 hover:bg-amber-700' },
              { key: 'scadute', label: 'Scadute', count: contatoriStato.scadute, tot: contatoriStato.totScadute, cls: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100', active: 'bg-red-600 text-white border-red-600 hover:bg-red-700' },
            ].map(f => {
              const isActive = filtroStato === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setFiltroStato(f.key)}
                  className={`inline-flex flex-col items-start rounded-lg border px-3 py-1.5 transition-colors ${isActive ? f.active : f.cls}`}
                  data-testid={`fatture-uscita-filtro-stato-${f.key}`}
                >
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-semibold">{f.label}</span>
                    <span className={`text-[10px] rounded-full px-1.5 py-0 ${isActive ? 'bg-white/20' : 'bg-white/70'}`}>{f.count}</span>
                  </div>
                  <span className={`text-[10px] mt-0.5 ${isActive ? 'opacity-90' : 'opacity-70'}`} data-testid={`fatture-uscita-filtro-tot-${f.key}`}>
                    € {(f.tot || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </button>
              );
            })}
          </div>
          {/* iter88: grafico Andamento mensile ultimi 12 mesi (Pagate vs Da pagare) */}
          <AndamentoMensileFatture
            fatture={fatture}
            testIdPrefix="fatture-uscita"
            accentColor="emerald"
            classifyStato={(f) => {
              if (!f || !f.data || typeof f.data !== 'string') return null;
              if (f.tipo_documento === 'nota_credito') return null;
              if (f.stato === 'stornata' || f.stato === 'annullata') return null;
              if (f.stato === 'incassata') return 'pagata';
              return 'da_pagare';
            }}
          />
          <SafeTableBoundary label="delle fatture emesse">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={bulkSel.isAllSelected}
                    onCheckedChange={bulkSel.toggleAll}
                    aria-label="Seleziona tutto"
                    data-testid="bulk-select-all-fatture-uscita"
                  />
                </TableHead>
                <TableHead>Protocollo</TableHead>
                <TableHead>N.</TableHead>
                <TableHead>Data</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead className="text-right">Imponibile</TableHead>
                <TableHead className="text-right">IVA</TableHead>
                <TableHead className="text-right">Totale</TableHead>
                <TableHead>Scadenza</TableHead>
                <TableHead>Stati</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fattureVisibili.length === 0 ? (
                <TableRow><TableCell colSpan={11} className="text-center text-slate-400 py-8">{(searchQ || filtroMese !== 'tutti' || filtroStato !== 'tutte') ? 'Nessuna fattura trovata con il filtro corrente' : (forceTipo === 'nota_credito' ? 'Nessuna nota di credito emessa' : 'Nessuna fattura in uscita')}</TableCell></TableRow>
              ) : raggruppaMese ? (
                gruppiMese.map(g => {
                  const isCollapsed = mesiCollassati.has(g.key);
                  return (
                    <React.Fragment key={g.key}>
                      <TableRow className="bg-slate-100 hover:bg-slate-200 cursor-pointer" onClick={() => toggleMese(g.key)} data-testid={`gruppo-mese-uscita-${g.key}`}>
                        <TableCell colSpan={11} className="py-2">
                          <div className="flex items-center gap-2 text-slate-700 font-medium">
                            {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            <span>{g.label}</span>
                            <span className="text-xs text-slate-500 font-normal">· {g.rows.length} fatture</span>
                            <span className="ml-auto text-sm font-mono text-slate-800">€ {formatEuro(g.totale)}</span>
                          </div>
                        </TableCell>
                      </TableRow>
                      {!isCollapsed && g.rows.map(f => renderFatturaRow(f))}
                    </React.Fragment>
                  );
                })
              ) : (
                fattureVisibili.map(f => renderFatturaRow(f))
              )}
            </TableBody>
          </Table>
          </SafeTableBoundary>
        </CardContent>
      </Card>

      {/* Dialog Nuova Fattura */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Receipt className="h-5 w-5" />{editingId ? 'Modifica Fattura' : 'Nuova Fattura in Uscita'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAdd} className="space-y-4 mt-4">
            {!forceTipo && (
              <div className="space-y-2">
                <Label>Tipo Documento</Label>
                <Select value={form.tipo_documento} onValueChange={v => setForm({...form, tipo_documento: v, riferimento_fattura_id: ''})}>
                  <SelectTrigger data-testid="fu-tipo-documento"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fattura">Fattura</SelectItem>
                    <SelectItem value="nota_credito">Nota di Credito (riduce il ricavo)</SelectItem>
                  </SelectContent>
                </Select>
                {form.tipo_documento === 'nota_credito' && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                    La nota di credito viene contabilizzata come <strong>importo negativo</strong> (riduce il ricavo del cantiere).
                  </p>
                )}
              </div>
            )}
            {form.tipo_documento === 'nota_credito' && (() => {
              const fattureCandidate = (tutteFatture || []).filter(f =>
                f.tipo_documento !== 'nota_credito' && !['annullata', 'stornata'].includes(f.stato),
              );
              const selectedFattura = fattureCandidate.find(f => f.id === form.riferimento_fattura_id);
              const q = String(riferimentoQuery || '').toLowerCase().trim();
              const fattureFiltrate = q
                ? fattureCandidate.filter(f => {
                    const importo = (f.importo || 0).toFixed(2);
                    const _s = (v) => String(v == null ? '' : v).toLowerCase();
                    return (
                      _s(f.numero).includes(q) ||
                      _s(f.cliente).includes(q) ||
                      _s(f.data).includes(q) ||
                      importo.includes(q)
                    );
                  })
                : fattureCandidate;
              return (
                <div className="space-y-2">
                  <Label>Fattura di riferimento *</Label>
                  <Popover open={riferimentoOpen} onOpenChange={(o) => { setRiferimentoOpen(o); if (!o) setRiferimentoQuery(''); }}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        className="w-full justify-between font-normal"
                        data-testid="fu-riferimento"
                      >
                        <span className={selectedFattura ? '' : 'text-slate-500'}>
                          {selectedFattura
                            ? `${selectedFattura.numero} · ${selectedFattura.cliente} · € ${(selectedFattura.importo || 0).toFixed(2)}`
                            : `Seleziona la fattura da stornare... (${fattureCandidate.length} disponibili)`}
                        </span>
                        <SearchIcon className="h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[560px] p-0" align="start">
                      <div className="p-2 border-b">
                        <Input
                          placeholder="Cerca per numero, cliente, data o importo…"
                          value={riferimentoQuery}
                          onChange={(e) => setRiferimentoQuery(e.target.value)}
                          className="h-9"
                          autoFocus
                          data-testid="fu-riferimento-search"
                        />
                      </div>
                      <div className="max-h-72 overflow-y-auto">
                        {fattureFiltrate.length === 0 ? (
                          <div className="px-3 py-6 text-xs text-slate-500 text-center">
                            {fattureCandidate.length === 0
                              ? 'Nessuna fattura disponibile da stornare.'
                              : 'Nessun risultato. Prova con un altro termine di ricerca.'}
                          </div>
                        ) : fattureFiltrate.map((f) => (
                          <button
                            key={f.id}
                            type="button"
                            onClick={() => handleSelectRiferimento(f.id)}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-slate-100 last:border-0 transition-colors ${
                              form.riferimento_fattura_id === f.id ? 'bg-blue-50' : ''
                            }`}
                            data-testid={`fu-riferimento-opt-${f.id}`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="font-mono text-xs text-slate-700">{f.numero}</p>
                                <p className="font-medium truncate text-slate-900">{f.cliente || 'Cliente n.d.'}</p>
                                <p className="text-[11px] text-slate-500">
                                  {f.data && fmtDate(f.data, 'dd-MM-yyyy')}
                                  {f.stato && ` · ${f.stato}`}
                                </p>
                              </div>
                              <span className="font-mono text-sm font-bold text-emerald-700 whitespace-nowrap">€ {(f.importo || 0).toFixed(2)}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                  {form.riferimento_fattura_id && (
                    <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded p-2">
                      La fattura selezionata verrà marcata come <strong>stornata</strong>. Resta visibile nel fatturato emesso ma viene rimossa da "Da Incassare".
                    </p>
                  )}
                </div>
              );
            })()}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5 col-span-2">
                <Label>Cliente *</Label>
                <Select value={form.cliente_id} onValueChange={handleClienteChange}>
                  <SelectTrigger data-testid="fattura-cliente-select"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
                  <SelectContent>
                    {clienti.map(c => <SelectItem key={c.id} value={c.id}>{c.ragione_sociale}</SelectItem>)}
                  </SelectContent>
                </Select>
                {!form.cliente_id && (
                  <Input placeholder="oppure scrivi manualmente" value={form.cliente} onChange={e => setForm({...form, cliente: e.target.value})} className="mt-1" data-testid="fattura-cliente-manual" />
                )}
              </div>
              <div className="space-y-1.5">
                <Label>Numero Fattura *</Label>
                <Input value={form.numero} onChange={e => setForm({...form, numero: e.target.value})} placeholder="Es: FT-001/2026" data-testid="fattura-numero" />
              </div>
              <div className="space-y-1.5">
                <Label>Data</Label>
                <Input type="date" value={form.data} onChange={e => setForm({...form, data: e.target.value})} data-testid="fattura-data" />
              </div>
              <div className="space-y-1.5">
                <Label>Imponibile *</Label>
                <Input type="number" step="0.01" value={form.imponibile} onChange={e => setForm({...form, imponibile: e.target.value})} placeholder="0.00" data-testid="fattura-imponibile" />
              </div>
              <div className="space-y-1.5">
                <Label>Aliquota IVA %</Label>
                <Select value={form.aliquota_iva} onValueChange={v => setForm({...form, aliquota_iva: v})}>
                  <SelectTrigger data-testid="fattura-aliquota"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0% (Esente)</SelectItem>
                    <SelectItem value="4">4%</SelectItem>
                    <SelectItem value="5">5%</SelectItem>
                    <SelectItem value="10">10%</SelectItem>
                    <SelectItem value="22">22%</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.imponibile && (
                <div className="col-span-2 grid grid-cols-3 gap-2 p-2.5 bg-slate-50 border border-slate-200 rounded">
                  <div className="text-center"><p className="text-[10px] uppercase text-slate-500">Imponibile</p><p className="font-mono text-sm font-semibold">{formatEuro(parseFloat(form.imponibile || 0))}</p></div>
                  <div className="text-center"><p className="text-[10px] uppercase text-slate-500">IVA {form.aliquota_iva}%</p><p className="font-mono text-sm font-semibold">{formatEuro(parseFloat(form.imponibile || 0) * parseFloat(form.aliquota_iva || VAT_DEFAULT_PERCENTAGE) / 100)}</p></div>
                  <div className="text-center bg-emerald-50 rounded"><p className="text-[10px] uppercase text-emerald-700">Totale</p><p className="font-mono text-sm font-bold text-emerald-700">{formatEuro(parseFloat(form.imponibile || 0) * (1 + parseFloat(form.aliquota_iva || VAT_DEFAULT_PERCENTAGE) / 100))}</p></div>
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Scadenza</Label>
                <Input type="date" value={form.scadenza} onChange={e => setForm({...form, scadenza: e.target.value})} data-testid="fattura-scadenza" />
              </div>
              <div className="space-y-1.5">
                <Label>Cantiere</Label>
                <CantiereSelectByCliente
                  cantieri={cantieri}
                  value={form.cantiere_id}
                  onChange={v => { setForm({...form, cantiere_id: v, acconto_riferimento: v ? form.acconto_riferimento : ''}); setSelectedContoId(''); }}
                  clienteId={form.cliente_id}
                  clienteNome={form.cliente}
                  placeholder="Opzionale — filtrato per cliente"
                  dataTestId="fattura-uscita-cantiere"
                />
                {form.cantiere_id && (
                  <div className="pt-2">
                    <AccontoSelector
                      value={form.acconto_riferimento}
                      onChange={v => setForm({...form, acconto_riferimento: v})}
                      label="Riferimento acconto (opzionale)"
                      hint="Assegna la fattura a un acconto del cantiere oppure segnalala come Extra (fuori preventivo). Lascia vuoto per non collegarla ad alcun acconto."
                      dataTestId="fattura-uscita-acconto"
                    />
                    {form.acconto_riferimento && (
                      <button
                        type="button"
                        className="text-xs text-slate-500 underline hover:text-slate-700 mt-1"
                        onClick={() => setForm({...form, acconto_riferimento: ''})}
                        data-testid="fattura-uscita-acconto-clear"
                      >Rimuovi riferimento acconto</button>
                    )}
                  </div>
                )}
              </div>
              {/* iter57/iter77: Conto Emesso di riferimento (qualsiasi cantiere) */}
              <div className="space-y-1.5 col-span-2">
                  <Label className="flex items-center gap-1.5">
                    <Receipt className="h-3.5 w-3.5 text-amber-600" />
                    Conto Emesso di riferimento
                    <span className="text-xs font-normal text-slate-500">(opzionale)</span>
                  </Label>
                  <Select value={selectedContoId || 'none'} onValueChange={v => setSelectedContoId(v === 'none' ? '' : v)}>
                    <SelectTrigger data-testid="fattura-conto-link"><SelectValue placeholder="Nessun collegamento" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Nessun collegamento —</SelectItem>
                      {contiDisponibili.map(c => {
                        const linkedToOther = c.fattura_uscita_id && c.fattura_uscita_id !== editingId;
                        const otherCantiere = !form.cantiere_id || c.cantiere_id !== form.cantiere_id;
                        return (
                          <SelectItem key={c.id} value={c.id} disabled={linkedToOther}>
                            <span className="font-mono">{c.numero}</span>
                            {' · '}
                            {c.data_emissione && fmtDate(c.data_emissione, 'dd-MM-yyyy')}
                            {' · €'}{((c.totale_lordo || c.prezzo_emesso || 0)).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                            {otherCantiere && c.cantiere_nome && ` · ${c.cantiere_nome}`}
                            {linkedToOther && ` (già collegato a ${c.fattura_uscita_numero})`}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  {contiDisponibili.length === 0 && (
                    <p className="text-xs text-slate-500">Nessun conto emesso disponibile</p>
                  )}
                </div>
              <div className="space-y-1.5 col-span-2">
                <Label>Descrizione</Label>
                <Textarea value={form.descrizione} onChange={e => setForm({...form, descrizione: e.target.value})} rows={2} placeholder="Descrizione lavori..." data-testid="fattura-descrizione" />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label>Allegato PDF/XML (opzionale)</Label>
                {form.file_url ? (
                  <div className="flex items-center justify-between p-2.5 bg-blue-50 border border-blue-200 rounded">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="h-4 w-4 text-blue-600 shrink-0" />
                      <span className="text-sm text-blue-900 truncate">File allegato</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button type="button" variant="ghost" size="sm" className="h-7" onClick={() => { setPreviewUrl(form.file_url); setPreviewTitle('Allegato'); setPreviewOpen(true); }} data-testid="fu-view-file"><Eye className="h-3.5 w-3.5" /></Button>
                      <Button type="button" variant="ghost" size="sm" className="h-7 text-red-600" onClick={() => { setForm(prev => ({...prev, file_url: ''})); setFile(null); }} data-testid="fu-remove-file">Rimuovi</Button>
                    </div>
                  </div>
                ) : (
                  <Input type="file" accept=".pdf,.xml,.jpg,.png" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="fattura-file" />
                )}
                {file && !form.file_url && (
                  <p className="text-xs text-slate-500 mt-1">Nuovo file selezionato: {file.name}</p>
                )}
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setDialogOpen(false)}>Annulla</Button>
              <Button type="submit" disabled={uploading} className="flex-1 bg-slate-900 text-white hover:bg-slate-800" data-testid="submit-fattura-uscita">
                {(() => {
                  if (uploading) return 'Caricamento...';
                  return editingId ? 'Salva modifiche' : 'Crea Fattura';
                })()}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-[90vw] sm:max-h-[90vh]">
          <DialogHeader><DialogTitle>{previewTitle}</DialogTitle></DialogHeader>
          <div className="mt-4 w-full" style={{ height: '70vh' }}>
            <FilePreview url={previewUrl} title={previewTitle} />
          </div>
        </DialogContent>
      </Dialog>

      {/* Cronologia Dialog */}
      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              <History className="h-4 w-4 inline mr-2" />
              Cronologia documento
              {logDoc && <span className="ml-2 text-xs font-mono text-slate-500">{logDoc.protocollo} · N. {logDoc.numero}</span>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 mt-2 max-h-96 overflow-y-auto">
            {logEntries.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">Nessuna voce di log</p>
            ) : logEntries.slice().reverse().map((e, i) => (
              <div key={`${e.timestamp || ''}-${e.action || ''}-${i}`} className="border border-slate-200 rounded p-3 bg-slate-50">
                <div className="flex items-center justify-between flex-wrap gap-1">
                  <Badge variant="outline" className="bg-white text-xs">{e.action}</Badge>
                  <span className="text-xs text-slate-500">{e.timestamp ? format(parseISO(e.timestamp), 'dd-MM-yyyy HH:mm:ss') : ''}</span>
                </div>
                <p className="text-sm text-slate-700 mt-1">{e.details}</p>
                <p className="text-xs text-slate-400 mt-1">da {e.user_name}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Pagamenti dialog (iter47) */}
      {pagamentiFattura && (
        <PagamentiDialog
          open={pagamentiOpen}
          onOpenChange={(v) => { setPagamentiOpen(v); if (!v) setPagamentiFattura(null); }}
          fattura={pagamentiFattura}
          tipo="fatture-uscita"
          onUpdated={fetchAll}
        />
      )}

      {/* iter85: Link cantiere anche con SDI-lock */}
      <Dialog open={!!linkCantiereFattura} onOpenChange={(v) => { if (!v) setLinkCantiereFattura(null); }}>
        <DialogContent className="max-w-md" data-testid="link-cantiere-dialog-uscita">
          <DialogHeader>
            <DialogTitle>Collega fattura a cantiere</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-slate-600">
              <div><span className="text-slate-400">Cliente:</span> <strong>{linkCantiereFattura?.cliente}</strong></div>
              <div><span className="text-slate-400">Numero:</span> <span className="font-mono">{linkCantiereFattura?.numero}</span></div>
              {linkCantiereFattura?.locked && (
                <div className="mt-2 flex items-center gap-1 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded p-2">
                  <Lock className="h-3 w-3" />
                  Fattura bloccata SDI: puoi collegare il cantiere ma i dati fiscali restano immutabili.
                </div>
              )}
            </div>
            <div>
              <Label>Cantiere</Label>
              <Select value={linkCantiereValue || '__none__'} onValueChange={v => setLinkCantiereValue(v === '__none__' ? '' : v)}>
                <SelectTrigger data-testid="link-cantiere-select-uscita"><SelectValue placeholder="Nessun cantiere" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Nessun cantiere —</SelectItem>
                  {cantieri.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name} {c.cliente_nome ? `· ${c.cliente_nome}` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {linkCantiereValue && (
              <div>
                <AccontoSelector
                  value={linkAccontoValue}
                  onChange={setLinkAccontoValue}
                  label="Riferimento acconto (opzionale)"
                  hint="Assegna la fattura a un acconto del cantiere oppure segnalala come Extra (fuori preventivo). Lascia vuoto per non collegarla ad alcun acconto."
                  dataTestId="link-cantiere-acconto-uscita"
                />
                {linkAccontoValue && (
                  <button
                    type="button"
                    className="mt-1 text-[11px] text-slate-500 hover:text-slate-700 underline"
                    onClick={() => setLinkAccontoValue('')}
                    data-testid="link-cantiere-acconto-clear-uscita"
                  >Rimuovi riferimento acconto</button>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setLinkCantiereFattura(null)}>Annulla</Button>
              <Button onClick={saveLinkCantiere} data-testid="link-cantiere-save-btn-uscita">Salva</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}